import React, { FunctionComponent } from 'react';

import _ from 'lodash';
import { DateTime } from 'luxon';
import { Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DatePickerWithLocale } from 'core/datepicker/DatePicker';
import { formatTimezoneOffset, getOffsetByTimezoneName, convertDateTimeToCalendarDate } from 'social/common/util/Util';
import { ScheduleDateObject } from 'social/publisher/Publisher.types';

import 'social/publisher/date-time-picker/single-date-time-picker/SingleDateTimePicker.scss';

interface Props {
    selectedScheduleDate: ScheduleDateObject;
    isUseLocationTimezoneEnabled: boolean;
    index: number;
    save: (selectedScheduleDate: ScheduleDateObject, index: number) => void;
    minDate: DateTime;
    maxDate: DateTime;
}

type TabOptions = {
    name: string;
    value: string;
};
const timezoneValues: TabOptions[] = [
    {
        name: 'GMT',
        value: '+00:00',
    },
    {
        name: 'PT',
        value: getOffsetByTimezoneName('America/Los_Angeles'),
    },
    {
        name: 'MT',
        value: getOffsetByTimezoneName('America/Denver'),
    },
    {
        name: 'CT',
        value: getOffsetByTimezoneName('America/Chicago'),
    },
    {
        name: 'ET',
        value: getOffsetByTimezoneName('America/New_York'),
    },
];
const useLocationTimezoneValues: TabOptions[] = [
    {
        name: 'SAME_TIME_AT_ALL_LOCATIONS',
        value: 'GLOBAL',
    },
    {
        name: 'LOCAL_TO_LOCATIONS',
        value: 'LOCAL',
    },
];

export const SingleDateTimePicker: FunctionComponent<Props> = ({
    selectedScheduleDate,
    isUseLocationTimezoneEnabled,
    index,
    save,
    minDate,
    maxDate,
}) => {
    const { t } = useTranslation(['social/publisher']);
    const { postDate, useLocationTimezone } = selectedScheduleDate;
    const isPrimary = index === 0;
    const globalOrLocal = useLocationTimezone ? 'LOCAL' : 'GLOBAL';
    const selectedTimezone = postDate.timezone;
    const selectedDate = new Date(postDate.year, postDate.month - 1, postDate.dayOfMonth);
    const selectedTime = new Date(
        postDate.year,
        postDate.month - 1,
        postDate.dayOfMonth,
        postDate.hour,
        postDate.minute,
        postDate.second,
    );

    const handleSetDateTimeToNow = (): void => {
        const newSelectedScheduleDate = _.cloneDeep(selectedScheduleDate);
        let now = DateTime.local();

        if (globalOrLocal === 'GLOBAL') {
            if (timezoneValues.findIndex((tz) => tz.value === selectedTimezone) > -1) {
                now = DateTime.local().setZone(`UTC${selectedTimezone}`);
            } else {
                now = DateTime.local().setZone('UTC');
            }
        }
        newSelectedScheduleDate.postDate = convertDateTimeToCalendarDate(now);
        save(newSelectedScheduleDate, index);
    };
    const saveSelectedDate = (date: Date): void => {
        const newSelectedScheduleDate = _.cloneDeep(selectedScheduleDate);

        newSelectedScheduleDate.postDate.year = date.getFullYear();
        newSelectedScheduleDate.postDate.month = date.getMonth() + 1;
        newSelectedScheduleDate.postDate.dayOfMonth = date.getDate();

        save(newSelectedScheduleDate, index);
    };
    const saveSelectedTime = (date: Date): void => {
        const newSelectedScheduleDate = _.cloneDeep(selectedScheduleDate);

        newSelectedScheduleDate.postDate.hour = date.getHours();
        newSelectedScheduleDate.postDate.minute = date.getMinutes();
        newSelectedScheduleDate.postDate.second = date.getSeconds();

        save(newSelectedScheduleDate, index);
    };
    const saveSelectedTimezone = (timezoneOffset: string): void => {
        const newSelectedScheduleDate = _.cloneDeep(selectedScheduleDate);

        newSelectedScheduleDate.postDate.timezone = timezoneOffset;

        save(newSelectedScheduleDate, index);
    };
    const saveGlobalOrLocal = (globalOrLocal: string): void => {
        const newSelectedScheduleDate = _.cloneDeep(selectedScheduleDate);

        newSelectedScheduleDate.useLocationTimezone = globalOrLocal === 'LOCAL';
        if (globalOrLocal === 'LOCAL') {
            newSelectedScheduleDate.postDate.timezone = formatTimezoneOffset(DateTime.local().offset);
        } else {
            newSelectedScheduleDate.postDate.timezone = formatTimezoneOffset(0);
        }
        save(newSelectedScheduleDate, index);
    };

    return (
        <div className="single-date-time-picker">
            {isPrimary && (
                <Button onClick={handleSetDateTimeToNow} variant="secondary" className="now-button">
                    {t('Now')}
                </Button>
            )}
            <DatePickerWithLocale
                selected={selectedDate}
                onChange={saveSelectedDate}
                minDate={minDate.toJSDate()}
                maxDate={maxDate.toJSDate()}
                placeholderText={t('SELECT_DATE')}
                dateFormat="P"
                popperPlacement="top"
                shouldCloseOnSelect={false}
                className="date-picker"
            />
            <DatePickerWithLocale
                selected={selectedTime}
                onChange={saveSelectedTime}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="p"
                popperPlacement="top"
                className="time-picker"
            />
            {isUseLocationTimezoneEnabled && (
                <>
                    <Dropdown>
                        <Dropdown.Toggle className="use-location-timezone" variant="default">
                            {t(_.get(_.find(useLocationTimezoneValues, { value: globalOrLocal }), 'name', 'TIMEZONE'))}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Header>{t('TIMEZONE')}</Dropdown.Header>
                            {useLocationTimezoneValues.map((ltz) => (
                                <Dropdown.Item
                                    active={globalOrLocal === ltz.value}
                                    value={ltz.value}
                                    key={ltz.name}
                                    onClick={(): void => {
                                        saveGlobalOrLocal(ltz.value);
                                    }}
                                >
                                    {t(ltz.name)}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    {isPrimary && (
                        <OverlayTrigger
                            placement="right"
                            overlay={
                                <Tooltip id="use-location-timezone-tooltip">
                                    {t('USE_LOCATION_TIMEZONE_TOOLTIP_LOCAL')}
                                    <br />
                                    {t('USE_LOCATION_TIMEZONE_TOOLTIP_LOCAL_EXAMPLE')}
                                    <br />
                                    <br />
                                    {t('USE_LOCATION_TIMEZONE_TOOLTIP_GLOBAL')}
                                    <br />
                                    {t('USE_LOCATION_TIMEZONE_TOOLTIP_GLOBAL_EXAMPLE')}
                                    <br />
                                    <br />
                                </Tooltip>
                            }
                        >
                            <i className="icon-info-circle" />
                        </OverlayTrigger>
                    )}

                    {globalOrLocal === 'GLOBAL' && (
                        <Dropdown>
                            <Dropdown.Toggle className="timezone" variant="default">
                                {_.get(_.find(timezoneValues, { value: selectedTimezone }), 'name', t('TIMEZONE'))}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Header>{t('TIMEZONE')}</Dropdown.Header>
                                {timezoneValues.map((tz) => (
                                    <Dropdown.Item
                                        active={selectedTimezone === tz.value}
                                        value={tz.value}
                                        key={tz.name}
                                        onClick={(): void => {
                                            saveSelectedTimezone(tz.value);
                                        }}
                                    >
                                        {tz.name}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </>
            )}
        </div>
    );
};
